import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

const PENDING_INVITE_KEY = 'pending_tournament_invite';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [inviteNotice, setInviteNotice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  useEffect(() => {
    if (inviteToken) {
      sessionStorage.setItem(PENDING_INVITE_KEY, inviteToken);
      setInviteNotice(
        'You have a tournament invitation. Sign in with the email address that received the invite to claim your spot.'
      );
    }
  }, [inviteToken]);

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.is_admin) {
        navigate('/dashboard');
      } else {
        navigate('/users');
      }
    }
  }, [isAuthenticated, user, navigate]);

  const claimPendingInvite = async () => {
    const token = sessionStorage.getItem(PENDING_INVITE_KEY);
    if (!token) return;
    sessionStorage.removeItem(PENDING_INVITE_KEY);
    try {
      await apiService.claimTournamentInvite(token);
      setInviteNotice('Your tournament spot has been confirmed.');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setInviteNotice(
        typeof detail === 'string'
          ? detail
          : 'Could not claim tournament invite. Make sure you sign in with the invited email address.'
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      await claimPendingInvite();
      const me = await apiService.getCurrentUser();
      navigate(me.data.is_admin ? '/dashboard' : '/users');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
          <img 
            src="/logo-no-text.png" 
            alt="PadPal Logo" 
            style={{ 
              width: 120, 
              height: 120, 
              marginBottom: 16 
            }} 
          />
          <Typography variant="h4" component="h1" gutterBottom>
            PadPal
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" gutterBottom>
            Sign in to your account
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          {inviteNotice && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {inviteNotice}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            autoComplete="email"
            autoFocus
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            autoComplete="current-password"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="medium"
            disabled={isLoading}
            sx={{ mt: 3, mb: 2 }}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
